<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

import ClosureReasonForm from './ClosureReasonForm.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeletePopup = ref(false);
const selectedReason = ref({});
const isDeleting = ref(false);

const records = computed(
  () => getters['closureReasons/getClosureReasons'].value
);
const uiFlags = computed(() => getters['closureReasons/getUIFlags'].value);

const typeLabels = computed(() => ({
  neutral: t('CLOSURE_REASONS.TYPES.NEUTRAL'),
  won: t('CLOSURE_REASONS.TYPES.WON'),
  lost: t('CLOSURE_REASONS.TYPES.LOST'),
}));

const typeClasses = {
  neutral: 'bg-n-slate-3 text-n-slate-11',
  won: 'bg-n-teal-3 text-n-teal-11',
  lost: 'bg-n-ruby-3 text-n-ruby-11',
};

const tableHeaders = computed(() => [
  t('CLOSURE_REASONS.TABLE.NAME'),
  t('CLOSURE_REASONS.TABLE.TYPE'),
  t('CLOSURE_REASONS.TABLE.ACTIONS'),
]);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = reason => {
  selectedReason.value = reason;
  showEditPopup.value = true;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = reason => {
  selectedReason.value = reason;
  showDeletePopup.value = true;
};
const closeDeletePopup = () => {
  showDeletePopup.value = false;
};

const confirmDeletion = async () => {
  isDeleting.value = true;
  try {
    await store.dispatch('closureReasons/delete', selectedReason.value.id);
    useAlert(t('CLOSURE_REASONS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error?.message || t('CLOSURE_REASONS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    isDeleting.value = false;
    closeDeletePopup();
  }
};

onBeforeMount(() => {
  store.dispatch('closureReasons/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('CLOSURE_REASONS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('CLOSURE_REASONS.NO_RECORDS')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('CLOSURE_REASONS.HEADER')"
        :description="$t('CLOSURE_REASONS.DESCRIPTION')"
        link-text=""
        feature-name="closure_reasons"
      >
        <template #actions>
          <Button
            :label="$t('CLOSURE_REASONS.ADD_BUTTON')"
            size="sm"
            icon="i-woot-plus"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="records"
        :no-data-message="$t('CLOSURE_REASONS.NO_RECORDS')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="reason in items" :key="reason.id" :item="reason">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12 font-medium">
                  {{ reason.name }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                  :class="typeClasses[reason.reason_type]"
                >
                  {{ typeLabels[reason.reason_type] }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openEditPopup(reason)"
                  />
                  <Button
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="openDeletePopup(reason)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <ClosureReasonForm @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <ClosureReasonForm
        :selected-record="selectedReason"
        is-edit
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CLOSURE_REASONS.DELETE.TITLE')"
      :message="$t('CLOSURE_REASONS.DELETE.MESSAGE')"
      :message-value="selectedReason.name"
      :confirm-text="$t('CLOSURE_REASONS.DELETE.CONFIRM')"
      :reject-text="$t('CLOSURE_REASONS.DELETE.REJECT')"
    />
  </SettingsLayout>
</template>
